"use client";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ExternalLink } from "lucide-react";
import { getContractExplorerUrl } from "@/lib/hoops-contracts";
import { StepCard } from "./StepCard";
import type { StepStatus } from "./types";

interface Step2Props {
  status: StepStatus;
  isConnected: boolean;
  activeAccountId: string;
  accountInput: string;
  setAccountInput: (v: string) => void;
  onDeploy: () => void;
  onInitialize: () => void;
  onUpgrade: () => void;
}

export function Step2DeployAccount({
  status,
  isConnected,
  activeAccountId,
  accountInput,
  setAccountInput,
  onDeploy,
  onInitialize,
  onUpgrade,
}: Step2Props) {
  const isProcessing = status === "in-progress";

  return (
    <StepCard
      stepNumber={2}
      title="Deploy Smart Account"
      description="Create your on-chain account contract"
      status={status}
      completedSummary={
        activeAccountId && (
          <a
            href={getContractExplorerUrl(activeAccountId)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 font-mono text-xs text-blue-500 hover:underline"
          >
            {activeAccountId.slice(0, 16)}...{activeAccountId.slice(-8)}
            <ExternalLink className="w-3 h-3" />
          </a>
        )
      }
    >
      <div className="space-y-4">
        <Input
          type="text"
          placeholder="Or enter existing account ID (C...)"
          value={accountInput}
          onChange={(e) => { setAccountInput(e.target.value); }}
          className="font-mono text-xs"
        />

        <div className="flex gap-2">
          <Button
            onClick={onDeploy}
            disabled={!isConnected || isProcessing}
            className="flex-1"
          >
            {isProcessing ? "Deploying..." : "Deploy New"}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={onInitialize}
            disabled={!isConnected || !accountInput || isProcessing}
          >
            Initialize
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={onUpgrade}
            disabled={!isConnected || !activeAccountId || isProcessing}
          >
            Upgrade
          </Button>
        </div>

        {activeAccountId && (
          <div className="bg-muted rounded-md p-3">
            <div className="text-xs text-muted-foreground mb-1">Active Account</div>
            <a
              href={getContractExplorerUrl(activeAccountId)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 font-mono text-xs text-blue-500 hover:underline break-all"
            >
              {activeAccountId}
              <ExternalLink className="w-3 h-3 shrink-0" />
            </a>
          </div>
        )}
      </div>
    </StepCard>
  );
}
